import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Row, Col, Typography } from 'antd';
import { useTranslation } from 'next-i18next';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';

const { Title, Paragraph, Text } = Typography;

interface SEOContentData {
  id: number;
  seo_content_type: string;
  action_ID: number | null;
  category_ID: number | null;
  location_type_ID: number | null;
  location_ID: number | null;
  branch_ID: number | null;
  PROVINCE_ID: number | null;
  AMPHUR_ID: number | null;
  content: string;
  content_status: number;
  paragraph: Array<{
    title: string;
    detail: string;
  }>;
}

interface SEOSectionProps {
  t?: any;
  seoContentData?: {
    status: number;
    count: number;
    data: SEOContentData[];
  } | null;
  isTablet?: boolean;
  isMobile?: boolean;
}

const SEOSection: React.FC<SEOSectionProps> = ({ 
  t: tProp, 
  seoContentData,
  isTablet: isTabletProp,
  isMobile: isMobileProp 
}) => {
  const { t: tFunc } = useTranslation('home');
  const t = tProp || tFunc;
  
  const { isMobile: deviceIsMobile, isTablet: deviceIsTablet } = useDeviceDetection();
  const isMobile = isMobileProp ?? deviceIsMobile;
  const isTablet = isTabletProp ?? deviceIsTablet;

  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);

  const fontFamily = {
    fontFamily: "'Sarabun', sans-serif"
  };

  // ใช้ข้อมูลจาก API หรือ fallback เป็นข้อมูลเดิม
  const paragraphs = seoContentData?.data?.[0]?.paragraph || [];
  const mainContent = seoContentData?.data?.[0]?.content || '';

  // คำนวณการแบ่ง columns ตามความสูง โดยใช้ useMemo แทน useEffect
  const columns = useMemo(() => {
    if (isMobile || paragraphs.length === 0) {
      return [paragraphs];
    }

    const maxHeight = isTablet ? 900 : 720; // tablet: 900px, desktop: 720px
    const tempColumns: Array<Array<{ title: string; detail: string }>> = [[]];
    let currentColumnIndex = 0;
    let currentHeight = 0;

    // เพิ่มหัวข้อหลักในคอลัมน์แรก (buying_selling_renting.title)
    const titleHeight = 60; // ประมาณความสูงของหัวข้อหลัก
    currentHeight += titleHeight;

    paragraphs.forEach((paragraph, index) => {
      // ประมาณความสูงของแต่ละ paragraph
      const titleHeight = 40; // ความสูงของ subtitle
      const contentHeight = Math.max(60, Math.ceil(paragraph.detail.length / 100) * 20); // ประมาณความสูงของเนื้อหา
      const paragraphHeight = titleHeight + contentHeight + 24; // รวม margin

      // ตรวจสอบว่าถ้าเพิ่ม paragraph นี้แล้วจะเกินความสูงไหม
      if (currentHeight + paragraphHeight > maxHeight && tempColumns[currentColumnIndex].length > 0) {
        // สร้างคอลัมน์ใหม่
        currentColumnIndex++;
        tempColumns[currentColumnIndex] = [];
        currentHeight = 0;
      }

      tempColumns[currentColumnIndex].push(paragraph);
      currentHeight += paragraphHeight;
    });

    return tempColumns;
  }, [paragraphs, isMobile, isTablet]);

  // Render paragraph content
  const renderParagraphContent = (paragraph: { title: string; detail: string }) => (
    <div key={paragraph.title} style={{ marginBottom: '24px' }}>
      <Title level={4} style={{ 
        fontSize: '14px', 
        fontWeight: 600, 
        color: '#000000',
        marginBottom: '12px',
        ...fontFamily
      }}>
        {paragraph.title}
      </Title>
      
      <div 
        style={{ 
          fontSize: '14px', 
          fontWeight: 400, 
          color: '#717172',
          ...fontFamily
        }}
        dangerouslySetInnerHTML={{ __html: paragraph.detail }}
      />
    </div>
  );

  // Render mobile layout (แสดงแบบตรงๆ)
  if (isMobile) {
    return (
      <div style={{ 
        backgroundColor: '#FFFFFF',
        padding: '48px 0',
        width: '100%',
        ...fontFamily
      }}>
        <div style={{ 
          maxWidth: '1496px', 
          margin: '0 auto',
          padding: '0 16px'
        }}>
          <Title level={3} style={{ 
            fontSize: '16px', 
            fontWeight: 600, 
            color: '#F4762A',
            marginBottom: '16px',
            ...fontFamily
          }}>
            {t('seo_section.buying_selling_renting.title')}
          </Title>

          {/* แสดง content หลักถ้ามี */}
          {/* {mainContent && (
            <div 
              style={{ 
                fontSize: '14px', 
                fontWeight: 400, 
                color: '#717172',
                marginBottom: '24px',
                ...fontFamily
              }}
              dangerouslySetInnerHTML={{ __html: mainContent }}
            />
          )} */}

          {/* แสดง paragraphs จาก API หรือ fallback เป็นข้อมูลเดิม */}
          {paragraphs.length > 0 ? (
            paragraphs.map(renderParagraphContent)
          ) : (
            <>
              <Title level={4} style={{ 
                fontSize: '14px', 
                fontWeight: 600, 
                color: '#000000',
                marginBottom: '12px',
                ...fontFamily
              }}>
                {t('seo_section.buying_selling_renting.subtitle')}
              </Title>
              
              <Paragraph style={{ 
                fontSize: '14px', 
                fontWeight: 400, 
                color: '#717172',
                marginBottom: '24px',
                ...fontFamily
              }}>
                {t('seo_section.buying_selling_renting.content')}
              </Paragraph>

              <Title level={3} style={{ 
                fontSize: '14px', 
                fontWeight: 600, 
                color: '#000000',
                marginBottom: '12px',
                ...fontFamily
              }}>
                {t('seo_section.investment.title')}
              </Title>
              
              <Paragraph style={{ 
                fontSize: '14px', 
                fontWeight: 400, 
                color: '#717172',
                marginBottom: '24px',
                ...fontFamily
              }}>
                {t('seo_section.investment.content')}
              </Paragraph>

              <Title level={3} style={{ 
                fontSize: '14px', 
                fontWeight: 600, 
                color: '#000000',
                marginBottom: '12px',
                ...fontFamily
              }}>
                {t('seo_section.choosing_website.title')}
              </Title>
              
              <Paragraph style={{ 
                fontSize: '14px', 
                fontWeight: 400, 
                color: '#717172',
                marginBottom: '24px',
                ...fontFamily
              }}>
                {t('seo_section.choosing_website.content')}
              </Paragraph>

              <Title level={3} style={{ 
                fontSize: '14px', 
                fontWeight: 600, 
                color: '#000000',
                marginBottom: '12px',
                ...fontFamily
              }}>
                {t('seo_section.benefits.title')}
              </Title>
              
              <Title level={5} style={{ 
                fontSize: '14px', 
                fontWeight: 600, 
                color: '#000000',
                marginBottom: '8px',
                marginTop: '16px',
                ...fontFamily
              }}>
                {t('seo_section.benefits.point1.title')}
              </Title>
              
              <Paragraph style={{ 
                fontSize: '14px', 
                fontWeight: 400, 
                color: '#717172',
                marginBottom: '16px',
                ...fontFamily
              }}>
                {t('seo_section.benefits.point1.content')}
              </Paragraph>
              
              <Title level={5} style={{ 
                fontSize: '14px', 
                fontWeight: 600, 
                color: '#000000',
                marginBottom: '8px',
                marginTop: '16px',
                ...fontFamily
              }}>
                {t('seo_section.benefits.point2.title')}
              </Title>
              
              <Paragraph style={{ 
                fontSize: '14px', 
                fontWeight: 400, 
                color: '#717172',
                marginBottom: '16px',
                ...fontFamily
              }}>
                {t('seo_section.benefits.point2.content')}
              </Paragraph>
              
              <Title level={5} style={{ 
                fontSize: '14px', 
                fontWeight: 600, 
                color: '#000000',
                marginBottom: '8px',
                marginTop: '16px',
                ...fontFamily
              }}>
                {t('seo_section.benefits.point3.title')}
              </Title>
              
              <Paragraph style={{ 
                fontSize: '14px', 
                fontWeight: 400, 
                color: '#717172',
                marginBottom: '16px',
                ...fontFamily
              }}>
                {t('seo_section.benefits.point3.content')}
              </Paragraph>

              <Title level={3} style={{ 
                fontSize: '14px', 
                fontWeight: 600, 
                color: '#000000',
                marginBottom: '12px',
                ...fontFamily
              }}>
                {t('seo_section.tips.title')}
              </Title>
              
              <Paragraph style={{ 
                fontSize: '14px', 
                fontWeight: 400, 
                color: '#717172',
                ...fontFamily
              }}>
                {t('seo_section.tips.content')}
              </Paragraph>
            </>
          )}
        </div>
      </div>
    );
  }

  // Render desktop/tablet layout (แบ่ง columns ตามความสูง)
  return (
    <div style={{ 
      backgroundColor: '#FFFFFF',
      padding: '96px 0',
      width: '100%',
      ...fontFamily
    }}>
      <div style={{ 
        maxWidth: '1496px', 
        margin: '0 auto',
        padding: '0 16px'
      }}>
        <Row gutter={32}>
          {paragraphs.length > 0 ? (
            // แสดงข้อมูลจาก API แบบแบ่ง columns
            columns.map((columnParagraphs, columnIndex) => (
              <Col xs={24} sm={24} md={24 / columns.length} key={columnIndex}>
                {columnIndex === 0 && (
                  <>
                    <Title level={3} style={{ 
                      fontSize: '16px', 
                      fontWeight: 600, 
                      color: '#F4762A',
                      marginBottom: '16px',
                      ...fontFamily
                    }}>
                      {t('seo_section.buying_selling_renting.title')}
                    </Title>

                    {/* แสดง content หลักในคอลัมน์แรก ถ้ามี */}
                    {mainContent && (
                      <div 
                        style={{ 
                          fontSize: '14px', 
                          fontWeight: 400, 
                          color: '#717172',
                          marginBottom: '24px',
                          ...fontFamily
                        }}
                        dangerouslySetInnerHTML={{ __html: mainContent }}
                      />
                    )}
                  </>
                )}
                
                {columnParagraphs.map(renderParagraphContent)}
              </Col>
            ))
          ) : (
            // Fallback เป็นข้อมูลเดิม
            <>
              <Col xs={24} sm={24} md={8}>
                <Title level={3} style={{ 
                  fontSize: '16px', 
                  fontWeight: 600, 
                  color: '#F4762A',
                  marginBottom: '16px',
                  ...fontFamily
                }}>
                  {t('seo_section.buying_selling_renting.title')}
                </Title>
                
                <Title level={4} style={{ 
                  fontSize: '14px', 
                  fontWeight: 600, 
                  color: '#000000',
                  marginBottom: '12px',
                  ...fontFamily
                }}>
                  {t('seo_section.buying_selling_renting.subtitle')}
                </Title>
                
                <Paragraph style={{ 
                  fontSize: '14px', 
                  fontWeight: 400, 
                  color: '#717172',
                  ...fontFamily
                }}>
                  {t('seo_section.buying_selling_renting.content')}
                </Paragraph>

                <Title level={3} style={{ 
                  fontSize: '14px', 
                  fontWeight: 600, 
                  color: '#000000',
                  marginBottom: '12px',
                  ...fontFamily
                }}>
                  {t('seo_section.investment.title')}
                </Title>
                
                <Paragraph style={{ 
                  fontSize: '14px', 
                  fontWeight: 400, 
                  color: '#717172',
                  marginBottom: '24px',
                  ...fontFamily
                }}>
                  {t('seo_section.investment.content')}
                </Paragraph>
              </Col>
              
              <Col xs={24} sm={24} md={8}>
                <Title level={3} style={{ 
                  fontSize: '14px', 
                  fontWeight: 600, 
                  color: '#000000',
                  marginBottom: '12px',
                  ...fontFamily
                }}>
                  {t('seo_section.choosing_website.title')}
                </Title>
                
                <Paragraph style={{ 
                  fontSize: '14px', 
                  fontWeight: 400, 
                  color: '#717172',
                  ...fontFamily
                }}>
                  {t('seo_section.choosing_website.content')}
                </Paragraph>

                <Title level={3} style={{ 
                  fontSize: '14px', 
                  fontWeight: 600, 
                  color: '#000000',
                  marginBottom: '12px',
                  ...fontFamily
                }}>
                  {t('seo_section.benefits.title')}
                </Title>
                
                <Title level={5} style={{ 
                  fontSize: '14px', 
                  fontWeight: 600, 
                  color: '#000000',
                  marginBottom: '8px',
                  marginTop: '16px',
                  ...fontFamily
                }}>
                  {t('seo_section.benefits.point1.title')}
                </Title>
                
                <Paragraph style={{ 
                  fontSize: '14px', 
                  fontWeight: 400, 
                  color: '#717172',
                  marginBottom: '16px',
                  ...fontFamily
                }}>
                  {t('seo_section.benefits.point1.content')}
                </Paragraph>
                
                <Title level={5} style={{ 
                  fontSize: '14px', 
                  fontWeight: 600, 
                  color: '#000000',
                  marginBottom: '8px',
                  marginTop: '16px',
                  ...fontFamily
                }}>
                  {t('seo_section.benefits.point2.title')}
                </Title>
                
                <Paragraph style={{ 
                  fontSize: '14px', 
                  fontWeight: 400, 
                  color: '#717172',
                  marginBottom: '16px',
                  ...fontFamily
                }}>
                  {t('seo_section.benefits.point2.content')}
                </Paragraph>
                
                <Title level={5} style={{ 
                  fontSize: '14px', 
                  fontWeight: 600, 
                  color: '#000000',
                  marginBottom: '8px',
                  marginTop: '16px',
                  ...fontFamily
                }}>
                  {t('seo_section.benefits.point3.title')}
                </Title>
                
                <Paragraph style={{ 
                  fontSize: '14px', 
                  fontWeight: 400, 
                  color: '#717172',
                  marginBottom: '24px',
                  ...fontFamily
                }}>
                  {t('seo_section.benefits.point3.content')}
                </Paragraph>
              </Col>
              
              <Col xs={24} sm={24} md={8}>
                <Title level={3} style={{ 
                  fontSize: '14px', 
                  fontWeight: 600, 
                  color: '#000000',
                  marginBottom: '12px',
                  ...fontFamily
                }}>
                  {t('seo_section.tips.title')}
                </Title>
                
                <Paragraph style={{ 
                  fontSize: '14px', 
                  fontWeight: 400, 
                  color: '#717172',
                  ...fontFamily
                }}>
                  {t('seo_section.tips.content')}
                </Paragraph>
              </Col>
            </>
          )}
        </Row>
      </div>
    </div>
  );
};

export { SEOSection };
export default SEOSection; 